import React, { useState, useEffect, useMemo } from "react";
import { 
    Card, Table, Row, Col, Form, InputGroup, Button, Badge, 
    Modal, Spinner, Alert, Tooltip, OverlayTrigger, Pagination 
} from "react-bootstrap";
import { Link, useNavigate } from "react-router-dom";
import Footer from "../layouts/Footer";
import Header from "../layouts/Header";
import { formatCurrency, formatDate, formatNumber } from "../utils/formatters";
import './Factures.css';
import { API_BASE_URL } from "../constants/api";
import logo from "../assets/img/IMG-20250130-WA0008.jpg";

export default function Avoirs() {
    const navigate = useNavigate();
    const [avoirs, setAvoirs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [filterText, setFilterText] = useState('');
    const [stats, setStats] = useState(null);
    const [showModal, setShowModal] = useState(false);
    const [selectedAvoir, setSelectedAvoir] = useState(null);
    const [selectedAvoirLignes, setSelectedAvoirLignes] = useState([]);
    const [loadingLignes, setLoadingLignes] = useState(false);
    const [sortConfig, setSortConfig] = useState({ key: 'date_facture', direction: 'desc' });
    const [societeInfos, setSocieteInfos] = useState(null);
    const [logoBase64, setLogoBase64] = useState('');
    
    const [taxes, setTaxes] = useState({
        tva_taux: 18,
        css_taux: 1,
        tva_active: true,
        css_active: true
    });

    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage] = useState(10);

    useEffect(() => {
        fetchAvoirs();
        fetchStats();
        fetchTaxes();
        fetchSocieteInfos();
        loadLogoBase64();
    }, []);

    const fetchSocieteInfos = async () => {
        try {
            const token = localStorage.getItem("token");
            const response = await fetch(
                `${API_BASE_URL}/parametres.php?action=societe`,
                { headers: { 'Authorization': `Bearer ${token}` } }
            );
            if (response.ok) {
                const result = await response.json();
                if (result.data) {
                    setSocieteInfos(result.data);
                }
            }
        } catch (err) {
            console.error("Erreur chargement société", err);
        }
    };

    const loadLogoBase64 = () => {
        return new Promise((resolve) => {
            try {
                const img = new Image();
                img.crossOrigin = "Anonymous";
                img.onload = () => {
                    const canvas = document.createElement('canvas');
                    canvas.width = img.width;
                    canvas.height = img.height;
                    const ctx = canvas.getContext('2d');
                    ctx.drawImage(img, 0, 0);
                    const dataURL = canvas.toDataURL('image/jpeg');
                    setLogoBase64(dataURL);
                    resolve(dataURL);
                };
                img.onerror = () => {
                    console.warn("Impossible de charger le logo");
                    setLogoBase64('');
                    resolve('');
                };
                img.src = logo;
            } catch (error) {
                console.error("Erreur chargement logo:", error);
                setLogoBase64('');
                resolve('');
            }
        });
    };

    const fetchTaxes = async () => {
        try {
            const token = localStorage.getItem("token");
            const response = await fetch(
                `${API_BASE_URL}/parametres.php?action=taxes`,
                { headers: { 'Authorization': `Bearer ${token}` } }
            );
            if (response.ok) {
                const result = await response.json();
                if (result.data) {
                    setTaxes(result.data);
                }
            }
        } catch (err) {
            console.error("Erreur chargement taxes", err);
        }
    };

    const fetchAvoirs = async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem("token");
            const response = await fetch(
                `${API_BASE_URL}/factures.php?type=avoir`,
                { headers: { 'Authorization': `Bearer ${token}` } }
            );
            
            if (!response.ok) throw new Error("Erreur chargement");
            
            const result = await response.json();
            setAvoirs(result.data || []);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const fetchAvoirLignes = async (idAvoir) => {
        setLoadingLignes(true);
        try {
            const token = localStorage.getItem("token");
            const response = await fetch(
                `${API_BASE_URL}/factures.php?id=${idAvoir}`,
                { headers: { 'Authorization': `Bearer ${token}` } }
            );
            
            if (!response.ok) throw new Error("Erreur chargement");
            
            const result = await response.json();
            setSelectedAvoirLignes(result.data?.lignes || []);
        } catch (err) {
            console.error("Erreur chargement lignes:", err);
            setSelectedAvoirLignes([]);
        } finally {
            setLoadingLignes(false);
        }
    };

    const fetchStats = async () => {
        try {
            const token = localStorage.getItem("token");
            const response = await fetch(
                `${API_BASE_URL}/factures.php?action=stats`,
                { headers: { 'Authorization': `Bearer ${token}` } }
            );
            
            if (response.ok) {
                const result = await response.json();
                setStats(result.data);
            }
        } catch (err) {
            console.error("Erreur stats", err);
        }
    };

    const filteredAvoirs = useMemo(() => {
        return avoirs
            .filter(a => 
                a.numero_facture?.toLowerCase().includes(filterText.toLowerCase()) ||
                a.client_nom?.toLowerCase().includes(filterText.toLowerCase()) ||
                a.notes?.toLowerCase().includes(filterText.toLowerCase())
            )
            .sort((a, b) => {
                if (sortConfig.key === 'date_facture') {
                    return sortConfig.direction === 'asc'
                        ? new Date(a.date_facture) - new Date(b.date_facture)
                        : new Date(b.date_facture) - new Date(a.date_facture);
                }
                if (sortConfig.key === 'montant') {
                    const montantA = a.net_a_payer || 0;
                    const montantB = b.net_a_payer || 0;
                    return sortConfig.direction === 'asc'
                        ? montantA - montantB
                        : montantB - montantA;
                }
                return 0;
            });
    }, [avoirs, filterText, sortConfig]);

    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    const currentItems = filteredAvoirs.slice(indexOfFirstItem, indexOfLastItem);
    const totalPages = Math.ceil(filteredAvoirs.length / itemsPerPage);

    const handlePageChange = (pageNumber) => {
        setCurrentPage(pageNumber);
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const renderPagination = () => {
        let items = [];
        for (let number = 1; number <= totalPages; number++) {
            items.push(
                <Pagination.Item 
                    key={number} 
                    active={number === currentPage}
                    onClick={() => handlePageChange(number)}
                >
                    {number}
                </Pagination.Item>
            );
        }
        return items;
    };

    const totalAvoirs = filteredAvoirs.reduce((acc, a) => {
        if (typeof a.net_a_payer === 'number') {
            return acc + a.net_a_payer;
        }
        const montant = parseFloat(String(a.net_a_payer).replace(/[^\d.-]/g, '')) || 0;
        return acc + montant;
    }, 0);

    const handleSort = (key) => {
        setSortConfig(prev => ({
            key,
            direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
        }));
    };

    const SortableHeader = ({ field, children }) => (
        <th 
            onClick={() => handleSort(field)}
            style={{ cursor: 'pointer', userSelect: 'none' }}
            className="sortable-header"
        >
            <div className="d-flex align-items-center gap-1">
                {children}
                {sortConfig.key === field && (
                    <i className={`ri-arrow-${sortConfig.direction === 'asc' ? 'up' : 'down'}-s-line`}></i>
                )}
            </div>
        </th>
    );

    const company = {
        name: societeInfos?.nom || 'GEOTECH',
        address: societeInfos?.adresse || 'Libreville, Gabon',
        phone: societeInfos?.telephone || '+241 00 00 00 00',
        email: societeInfos?.email || 'contact@geotech.com',
        site_web: societeInfos?.site_web || 'www.geotech.com',
        rc: societeInfos?.rc || 'RC123456',
        nif: societeInfos?.nif || 'NIF123456',
        patente: societeInfos?.patente || 'PAT123456'
    };

    const genererHTMLAvoir = (avoir, avoirLignes) => {
        const totalHT = avoir.montant_ht || 0;
        const totalTVA = avoir.montant_tva || 0;
        const totalCSS = avoir.montant_css || 0;
        const totalTTC = avoir.net_a_payer || 0;
        const logoSrc = logoBase64;
        const logoText = company.name.substring(0, 2).toUpperCase();

        return `
            <!DOCTYPE html>
            <html lang="fr">
            <head>
                <meta charset="UTF-8">
                <meta name="viewport" content="width=device-width, initial-scale=1.0">
                <title>Avoir ${avoir.numero_facture}</title>
                <style>
                    * { margin: 0; padding: 0; box-sizing: border-box; }
                    body { 
                        font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
                        background: #eef2f7;
                        padding: 20px;
                        color: #1e293b;
                        font-size: 11px;
                    }
                    .invoice-container {
                        margin: 0 auto;
                        background: white;
                        border-radius: 12px;
                        overflow: hidden;
                    }
                    .invoice-header {
                        background: white;
                        padding: 5px;
                        border-bottom: 1px solid #e2e8f0;
                    }
                    .header-top {
                        display: flex;
                        justify-content: space-between;
                        align-items: center;
                        margin-bottom: 15px;
                    }
                    .logo-area {
                        display: flex;
                        align-items: center;
                        gap: 12px;
                    }
                    .logo-img {
                        width: auto;
                        height: 60px;
                    }
                    .logo-fallback {
                        width: 50px;
                        height: 50px;
                        background: linear-gradient(135deg, #f59e0b, #d97706);
                        border-radius: 10px;
                        display: flex;
                        align-items: center;
                        justify-content: center;
                        font-size: 20px;
                        font-weight: bold;
                        color: white;
                    }
                    .company-name {
                        font-size: 16px;
                        font-weight: 700;
                        color: #1a3c5e;
                    }
                    .invoice-title {
                        text-align: right;
                    }
                    .invoice-title h2 {
                        font-size: 18px;
                        font-weight: 700;
                        color: #f59e0b;
                        letter-spacing: 1px;
                        margin-bottom: 4px;
                    }
                    .invoice-number {
                        font-size: 10px;
                        color: #64748b;
                    }
                    .status-badge {
                        display: inline-block;
                        padding: 3px 10px;
                        border-radius: 20px;
                        font-size: 9px;
                        font-weight: 600;
                        margin-top: 6px;
                        background: #fef3c7;
                        color: #92400e;
                    }
                    .company-details {
                        display: flex;
                        justify-content: space-between;
                        font-size: 9px;
                        color: #64748b;
                        padding: 5px;
                        border-top: 1px solid #e2e8f0;
                    }
                    .invoice-body {
                        padding: 5px 0px;
                    }
                    .client-card {
                        background: #f8fafc;
                        border-radius: 10px;
                        padding: 15px;
                        margin-bottom: 20px;
                        display: flex;
                        justify-content: space-between;
                        align-items: flex-start;
                        border: 1px solid #e2e8f0;
                    }
                    .info-label {
                        font-size: 9px;
                        font-weight: 600;
                        text-transform: uppercase;
                        letter-spacing: 0.5px;
                        color: #f59e0b;
                        margin-bottom: 6px;
                    }
                    .client-name {
                        font-size: 14px;
                        font-weight: 700;
                        color: #0f172a;
                        margin-bottom: 6px;
                    }
                    .client-details {
                        font-size: 10px;
                        color: #475569;
                        line-height: 1.4;
                    }
                    .date-item {
                        margin-bottom: 6px;
                    }
                    .date-label {
                        font-size: 8px;
                        color: #64748b;
                    }
                    .date-value {
                        font-size: 11px;
                        font-weight: 600;
                        color: #0f172a;
                    }
                    .notes-box {
                        margin-top: 15px;
                        padding: 10px;
                        background: #fef3c7;
                        border-left: 3px solid #f59e0b;
                        border-radius: 6px;
                        font-size: 9px;
                        color: #92400e;
                    }
                    .products-table {
                        width: 100%;
                        border-collapse: collapse;
                        margin: 15px 0;
                        font-size: 10px;
                    }
                    .products-table th {
                        background: #f8fafc;
                        padding: 8px 10px;
                        text-align: left;
                        font-weight: 600;
                        color: #1e293b;
                        border-bottom: 1.5px solid #e2e8f0;
                    }
                    .products-table td {
                        padding: 8px 10px;
                        border-bottom: 1px solid #e2e8f0;
                        color: #334155;
                    }
                    .text-right { text-align: right; }
                    .text-center { text-align: center; }
                    .totals {
                        display: flex;
                        justify-content: flex-end;
                        margin-top: 15px;
                    }
                    .totals-card {
                        width: 280px;
                        background: #f8fafc;
                        border-radius: 10px;
                        padding: 15px;
                    }
                    .total-line {
                        display: flex;
                        justify-content: space-between;
                        padding: 5px 0;
                        font-size: 10px;
                        border-bottom: 1px dashed #e2e8f0;
                    }
                    .total-line.highlight {
                        background: #fef3c7;
                        margin: 8px -15px;
                        padding: 8px 15px;
                        font-weight: 600;
                    }
                    .total-line.final {
                        background: #fef2f2;
                        margin-top: 8px;
                        padding: 8px 15px;
                        border-radius: 8px;
                    }
                    .signature-section {
                        margin-top: 25px;
                        padding-top: 15px;
                        border-top: 1px solid #e2e8f0;
                        display: flex;
                        justify-content: space-between;
                        align-items: flex-end;
                    }
                    .signature-box {
                        text-align: center;
                    }
                    .signature-line {
                        width: 150px;
                        height: 1px;
                        background: #1a3c5e;
                        margin-bottom: 6px;
                    }
                    .signature-label {
                        font-size: 8px;
                        color: #64748b;
                    }
                    .invoice-footer {
                        background: #f8fafc;
                        padding: 12px 25px;
                        text-align: center;
                        font-size: 8px;
                        color: #64748b;
                        border-top: 1px solid #e2e8f0;
                    }
                    @media print {
                        body { background: white; padding: 0; }
                        .invoice-container { box-shadow: none; border-radius: 0; }
                    }
                </style>
            </head>
            <body>
                <div class="invoice-container">
                    <div class="invoice-header">
                        <div class="header-top">
                            <div class="logo-area">
                                ${logoSrc ? `<img src="${logoSrc}" alt="Logo" class="logo-img" />` : `<div class="logo-fallback">${logoText}</div>`}
                            </div>
                            <div class="invoice-title">
                                <h2>AVOIR</h2>
                                <div class="invoice-number">N° ${avoir.numero_facture}</div>
                                <div class="status-badge">NOTE DE CRÉDIT</div>
                            </div>
                        </div>
                        <div class="company-details">
                            <table style="width: 100%; border: 0px;">
                                <tr>
                                    <td style="padding: 6px 8px; border: 0px; width: 50%;">
                                        <strong>Tél:</strong> ${company.phone}
                                    </td>
                                    <td style="padding: 6px 8px; border: 0px; width: 50%;">
                                        <strong>Adresse:</strong> ${company.address}
                                    </td>
                                </tr>
                                <tr>
                                    <td style="padding: 6px 8px; border: 0px;">
                                        <strong>Email:</strong> ${company.email}
                                    </td>
                                    <td style="padding: 6px 8px; border: 0px;">
                                        <strong>Site web:</strong> ${company.site_web}
                                    </td>
                                </tr>
                            </table>
                        </div>
                    </div>
                    
                    <div class="invoice-body">
                        <div class="client-card">
                            <div>
                                <div class="info-label">CLIENT</div>
                                <div class="client-name">${avoir.client_nom || 'Client non spécifié'}</div>
                                <div class="client-details">
                                    <div>Code: ${avoir.code_client || ''}</div>
                                    ${avoir.client_telephone ? `<div>📞 ${avoir.client_telephone}</div>` : ''}
                                    ${avoir.client_email ? `<div>✉ ${avoir.client_email}</div>` : ''}
                                </div>
                            </div>
                            <div class="dates">
                                <div class="date-item">
                                    <div class="date-label">Date d'émission</div>
                                    <div class="date-value">${formatDate(avoir.date_facture)}</div>
                                </div>
                            </div>
                        </div>

                        ${avoir.notes ? `
                        <div class="notes-box">
                            <strong>📝 Motif :</strong> ${avoir.notes}
                        </div>
                        ` : ''}

                        <table class="products-table">
                            <thead>
                                <tr>
                                    <th>Réf.</th>
                                    <th>Désignation</th>
                                    <th class="text-center">Qté</th>
                                    <th class="text-right">Prix HT</th>
                                    <th class="text-right">Total HT</th>
                                </tr>
                            </thead>
                            <tbody>
                                ${avoirLignes && avoirLignes.length > 0 
                                    ? avoirLignes.map(ligne => `
                                        <tr>
                                            <td><span style="font-family: monospace;">${ligne.reference || ''}</span></td>
                                            <td>${ligne.designation || ''}</td>
                                            <td class="text-center">${ligne.quantite || 0}</td>
                                            <td class="text-right">${formatCurrency(ligne.prix_unitaire_ht || 0)}</td>
                                            <td class="text-right"><strong>${formatCurrency((ligne.quantite || 0) * (ligne.prix_unitaire_ht || 0))}</strong></td>
                                        </tr>
                                    `).join('')
                                    : '<tr><td colspan="5" class="text-center">Aucun produit dans cet avoir</td></tr>'
                                }
                            </tbody>
                        </table>
                        
                        <div class="totals">
                            <div class="totals-card">
                                <div class="total-line">
                                    <span>Total HT</span>
                                    <span><strong>${formatCurrency(totalHT)}</strong></span>
                                </div>
                                ${taxes.tva_active && totalTVA > 0 ? `
                                <div class="total-line">
                                    <span>TVA (${taxes.tva_taux}%)</span>
                                    <span>${formatCurrency(totalTVA)}</span>
                                </div>
                                ` : ''}
                                ${taxes.css_active && totalCSS > 0 ? `
                                <div class="total-line">
                                    <span>CSS (${taxes.css_taux}%)</span>
                                    <span>${formatCurrency(totalCSS)}</span>
                                </div>
                                ` : ''}
                                <div class="total-line highlight">
                                    <span><strong>TOTAL TTC</strong></span>
                                    <span><strong style="color: #f59e0b;">-${formatCurrency(totalTTC)}</strong></span>
                                </div>
                            </div>
                        </div>

                        <div class="signature-section">
                            <div class="signature-box">
                                <div class="signature-line"></div>
                                <div class="signature-label">Signature client</div>
                            </div>
                            <div class="signature-box">
                                <div class="signature-line"></div>
                                <div class="signature-label">Signature et cachet</div>
                            </div>
                        </div>
                    </div>
                    
                    <div class="invoice-footer">
                        <div>Note de crédit — Avoir à déduire sur les prochaines factures</div>
                        <div style="margin-top: 4px;">${company.name} • ${company.phone} • ${company.email}</div>
                    </div>
                </div>
            </body>
            </html>
        `;
    };

    const handlePrintAvoir = (avoir) => {
        const fetchAndPrint = async () => {
            let avoirLignes = [];
            try {
                const token = localStorage.getItem("token");
                const response = await fetch(
                    `${API_BASE_URL}/factures.php?id=${avoir.id_facture}`,
                    { headers: { 'Authorization': `Bearer ${token}` } }
                );
                if (response.ok) {
                    const result = await response.json();
                    avoirLignes = result.data?.lignes || [];
                }
            } catch (err) {
                console.error("Erreur chargement lignes:", err);
            }
            
            const htmlContent = genererHTMLAvoir(avoir, avoirLignes);
            const iframe = document.createElement('iframe');
            iframe.style.position = 'absolute';
            iframe.style.width = '0';
            iframe.style.height = '0';
            iframe.style.border = 'none';
            document.body.appendChild(iframe);
            
            const iframeDoc = iframe.contentWindow.document;
            iframeDoc.open();
            iframeDoc.write(htmlContent);
            iframeDoc.close();
            
            iframe.contentWindow.onload = () => {
                iframe.contentWindow.print();
                setTimeout(() => {
                    if (document.body.contains(iframe)) document.body.removeChild(iframe);
                }, 1000);
            };
        };
        
        fetchAndPrint();
    };

    const ActionButton = ({ variant, icon, onClick, tooltip, disabled = false }) => (
        <OverlayTrigger
            placement="top"
            overlay={<Tooltip id={`tooltip-${variant}`}>{tooltip}</Tooltip>}
        >
            <Button
                variant={variant}
                size="sm"
                onClick={onClick}
                disabled={disabled}
                className="action-btn"
            >
                <i className={icon}></i>
            </Button>
        </OverlayTrigger>
    );

    if (loading) {
        return (
            <>
                <Header />
                <div className="main main-app p-3 p-lg-4" style={{ background: '#f8fafc', minHeight: '100vh' }}>
                    <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '60vh' }}>
                        <div className="text-center">
                            <div className="loading-animation mb-4">
                                <div className="loading-ring"><div className="loading-ring-inner"></div></div>
                                <i className="ri-file-copy-line loading-icon"></i>
                            </div>
                            <h5 className="fw-bold text-primary mb-2">Chargement des avoirs</h5>
                            <p className="text-muted small mb-0">Récupération des données en cours...</p>
                            <div className="mx-auto mt-4" style={{ width: '200px' }}>
                                <div className="progress" style={{ height: '3px', background: '#e9ecef' }}>
                                    <div className="progress-bar progress-bar-striped progress-bar-animated" style={{ width: '100%', background: 'linear-gradient(90deg, #f59e0b, #d97706, #b45309)' }} />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <Footer />
                <style>{`
                    .loading-animation { position: relative; width: 80px; height: 80px; margin: 0 auto; }
                    .loading-ring { position: absolute; top: 0; left: 0; width: 100%; height: 100%; border-radius: 50%; background: linear-gradient(135deg, #f59e0b, #d97706); animation: pulse-ring 1.5s ease-in-out infinite; }
                    .loading-ring-inner { position: absolute; top: 5px; left: 5px; right: 5px; bottom: 5px; border-radius: 50%; background: white; }
                    .loading-icon { position: absolute; top: 50%; left: 50%; transform: translate(-50%, -50%); font-size: 28px; color: #f59e0b; z-index: 2; animation: pulse-icon 1.5s ease-in-out infinite; }
                    @keyframes pulse-ring { 0% { transform: scale(0.9); opacity: 0.7; } 50% { transform: scale(1.1); opacity: 0.3; } 100% { transform: scale(0.9); opacity: 0.7; } }
                    @keyframes pulse-icon { 0%, 100% { transform: translate(-50%, -50%) scale(1); opacity: 1; } 50% { transform: translate(-50%, -50%) scale(1.1); opacity: 0.8; } }
                `}</style>
            </>
        );
    }

    if (error) {
        return (
            <>
                <Header />
                <div className="main main-app p-3 p-lg-4">
                    <Alert variant="danger" className="border-0 shadow-sm rounded-3">
                        <div className="d-flex align-items-center">
                            <i className="ri-error-warning-line fs-1 me-3 text-danger"></i>
                            <div>
                                <h6 className="mb-1 fw-bold">Erreur de chargement</h6>
                                <p className="mb-0 small">{error}</p>
                                <Button variant="outline-danger" size="sm" className="mt-2 rounded-pill" onClick={fetchAvoirs}>
                                    <i className="ri-refresh-line me-1"></i>Réessayer
                                </Button>
                            </div>
                        </div>
                    </Alert>
                </div>
                <Footer />
            </>
        );
    }

    return (
        <React.Fragment>
            <Header />
            <div className="main main-app p-3 p-lg-4" style={{ background: '#f8fafc', minHeight: '100vh' }}>
                <div className="container-fluid">
                    {/* En-tête */}
                    <div className="d-md-flex align-items-center justify-content-between mb-4">
                        <div>
                            <div className="d-flex align-items-center gap-2 mb-2">
                                <div className="p-2 bg-warning bg-opacity-10 rounded-3">
                                    <i className="ri-file-copy-line fs-20 text-warning"></i>
                                </div>
                                <div>
                                    <h4 className="main-title mb-0 fw-bold">Gestion des avoirs</h4>
                                    <p className="text-muted small mb-0">Consultez et gérez les avoirs et notes de crédit</p>
                                </div>
                            </div>
                        </div>
                        <div className="d-flex gap-2 mt-3 mt-md-0">
                            <Button 
                                variant="outline-primary" 
                                onClick={() => navigate('/ventes/factures')}
                                className="rounded-pill px-3 d-flex align-items-center gap-1"
                                size="sm"
                            >
                                <i className="ri-arrow-left-line"></i>
                                Retour aux factures
                            </Button>
                        </div>
                    </div>

                    {/* KPI Cards */}
                    <Row className="g-3 mb-4">
                        <Col sm="6" xl="3">
                            <div className="kpi-card kpi-warning">
                                <div className="kpi-icon"><i className="ri-file-copy-line fs-24"></i></div>
                                <div className="kpi-content">
                                    <span className="kpi-label">Total avoirs</span>
                                    <h3 className="kpi-value">{formatCurrency(totalAvoirs)}</h3>
                                    <span className="kpi-unit">{filteredAvoirs.length} avoir(s)</span>
                                </div>
                            </div>
                        </Col>
                        <Col sm="6" xl="3">
                            <div className="kpi-card kpi-info">
                                <div className="kpi-icon"><i className="ri-bar-chart-2-line fs-24"></i></div>
                                <div className="kpi-content">
                                    <span className="kpi-label">Moyenne par avoir</span>
                                    <h3 className="kpi-value">
                                        {filteredAvoirs.length > 0 ? formatCurrency(totalAvoirs / filteredAvoirs.length) : formatCurrency(0)}
                                    </h3>
                                    <span className="kpi-unit">montant moyen</span>
                                </div>
                            </div>
                        </Col>
                        <Col sm="6" xl="3">
                            <div className="kpi-card kpi-primary">
                                <div className="kpi-icon"><i className="ri-group-line fs-24"></i></div>
                                <div className="kpi-content">
                                    <span className="kpi-label">Clients concernés</span>
                                    <h3 className="kpi-value">{new Set(avoirs.map(a => a.id_client)).size}</h3>
                                    <span className="kpi-unit">différents clients</span>
                                </div>
                            </div>
                        </Col>
                        <Col sm="6" xl="3">
                            <div className="kpi-card kpi-success">
                                <div className="kpi-icon"><i className="ri-percent-line fs-24"></i></div>
                                <div className="kpi-content">
                                    <span className="kpi-label">TVA totale</span>
                                    <h3 className="kpi-value">
                                        {formatCurrency(avoirs.reduce((acc, a) => acc + (a.montant_tva || 0), 0))}
                                    </h3>
                                    <span className="kpi-unit">{taxes.tva_active ? taxes.tva_taux : 0}% moyen</span>
                                </div>
                            </div>
                        </Col>
                    </Row>

                    {/* Filtres */}
                    <Card className="border-0 shadow-sm rounded-3 mb-4">
                        <Card.Body className="py-3 px-4">
                            <Row className="align-items-center g-3">
                                <Col md="8">
                                    <div className="position-relative">
                                        <i className="ri-search-line position-absolute top-50 start-0 translate-middle-y ms-3 text-muted fs-14"></i>
                                        <Form.Control
                                            type="text"
                                            placeholder="Rechercher avoir, client, motif..."
                                            value={filterText}
                                            onChange={(e) => setFilterText(e.target.value)}
                                            className="ps-5 rounded-pill"
                                        />
                                    </div>
                                </Col>
                                <Col md="4">
                                    <div className="text-end">
                                        <span className="badge bg-light text-dark rounded-pill px-3 py-2">
                                            <i className="ri-stack-line me-1"></i>
                                            {filteredAvoirs.length} avoir(s)
                                        </span>
                                    </div>
                                </Col>
                            </Row>
                        </Card.Body>
                    </Card>

                    {/* Tableau des avoirs */}
                    <Card className="border-0 shadow-sm rounded-3 overflow-hidden">
                        <Card.Body className="p-0">
                            <div className="table-responsive">
                                <Table hover className="align-middle mb-0">
                                    <thead className="bg-light">
                                        <tr>
                                            <th className="ps-4">N° Avoir</th>
                                            <SortableHeader field="date_facture">Date</SortableHeader>
                                            <th>Client</th>
                                            <th className="text-end">Montant HT</th>
                                            <th className="text-end">TVA</th>
                                            <th className="text-end">Total TTC</th>
                                            <th className="text-center pe-4">Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {currentItems.length > 0 ? (
                                            currentItems.map((avoir) => (
                                                <tr key={avoir.id_facture} className="facture-row">
                                                    <td className="ps-4 fw-semibold">
                                                        <span className="table-link" style={{ cursor: 'pointer', color: '#f59e0b' }} onClick={() => {
                                                            setSelectedAvoir(avoir);
                                                            fetchAvoirLignes(avoir.id_facture);
                                                            setShowModal(true);
                                                        }}>
                                                            <i className="ri-file-copy-line me-1"></i>
                                                            {avoir.numero_facture}
                                                        </span>
                                                    </td>
                                                    <td className="text-muted">{formatDate(avoir.date_facture)}</td>
                                                    <td>
                                                        <div className="fw-semibold">{avoir.client_nom}</div>
                                                        <small className="text-muted">{avoir.code_client}</small>
                                                    </td>
                                                    <td class="text-end">{formatCurrency(avoir.montant_ht)}</td>
                                                    <td class="text-end text-warning">{formatCurrency(avoir.montant_tva)}</td>
                                                    <td class="text-end text-danger fw-bold">-{formatCurrency(avoir.net_a_payer)}</td>
                                                    <td class="text-center pe-4">
                                                        <div className="d-flex gap-2 justify-content-center">
                                                            <button className="action-icon" onClick={() => {
                                                                setSelectedAvoir(avoir);
                                                                fetchAvoirLignes(avoir.id_facture);
                                                                setShowModal(true);
                                                            }} title="Voir détails">
                                                                <i className="ri-eye-line"></i>
                                                            </button>
                                                            <button className="action-icon action-icon-primary" onClick={() => handlePrintAvoir(avoir)} title="Imprimer">
                                                                <i className="ri-printer-line"></i>
                                                            </button>
                                                        </div>
                                                    </td>
                                                </tr>
                                            ))
                                        ) : (
                                            <tr>
                                                <td colSpan="7" className="text-center py-5">
                                                    <i className="ri-inbox-line fs-48 text-muted mb-3 d-block"></i>
                                                    <h6 className="mb-1">Aucun avoir trouvé</h6>
                                                    <p className="text-muted small mb-0">Aucun avoir n'a été émis pour le moment</p>
                                                    {filterText && (
                                                        <Button variant="outline-primary" size="sm" className="mt-3 rounded-pill" onClick={() => setFilterText('')}>
                                                            <i className="ri-refresh-line me-1"></i>Réinitialiser
                                                        </Button>
                                                    )}
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </Table>
                            </div>

                            {/* Pagination */}
                            {totalPages > 1 && !loading && (
                                <div className="d-flex justify-content-between align-items-center mt-4 px-3">
                                    <div className="text-muted small">
                                        Affichage de {indexOfFirstItem + 1} à {Math.min(indexOfLastItem, filteredAvoirs.length)} sur {filteredAvoirs.length} avoir(s)
                                    </div>
                                    <Pagination className="mb-0">
                                        <Pagination.Prev onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage === 1} />
                                        {renderPagination()}
                                        <Pagination.Next onClick={() => handlePageChange(currentPage + 1)} disabled={currentPage === totalPages} />
                                    </Pagination>
                                </div>
                            )}
                        </Card.Body>
                    </Card>
                </div>
                <Footer />
            </div>

            {/* Modal de détail */}
            <Modal show={showModal} onHide={() => setShowModal(false)} size="lg" centered className="modern-modal">
                <Modal.Header closeButton className="border-0 pt-4 px-4" style={{background:'#1DACCE'}}>
                    <div className="d-flex align-items-center gap-2">
                        <div className="p-2 rounded-3 bg-warning bg-opacity-10"><i className="ri-file-copy-line fs-20 text-warning"></i></div>
                        <Modal.Title className="fs-5 fw-bold">Détail de l'avoir</Modal.Title>
                    </div>
                </Modal.Header>
                <Modal.Body className="px-4 pb-4">
                    {selectedAvoir && (
                        <>
                            <div className="summary-card p-3 mb-4 rounded-3">
                                <div className="d-flex justify-content-between align-items-start">
                                    <div>
                                        <h5 className="fw-bold mb-1">{selectedAvoir.numero_facture}</h5>
                                        <p className="text-muted small mb-0">
                                            <i className="ri-calendar-line me-1"></i>
                                            Date: {formatDate(selectedAvoir.date_facture)}
                                        </p>
                                    </div>
                                    <Badge bg="warning" className="px-3 py-2 rounded-pill">
                                        <i className="ri-file-copy-line me-1"></i> Avoir
                                    </Badge>
                                </div>
                            </div>

                            <div className="info-card p-3 mb-4 rounded-3">
                                <h6 className="fw-semibold mb-2 text-primary">Client</h6>
                                <p className="fw-bold mb-1">{selectedAvoir.client_nom}</p>
                                <p className="text-muted small mb-0">Code: {selectedAvoir.code_client}</p>
                                {selectedAvoir.client_telephone && (
                                    <p className="text-muted small mt-1"><i className="ri-phone-line me-1"></i>{selectedAvoir.client_telephone}</p>
                                )}
                            </div>

                            {selectedAvoir.notes && (
                                <div className="notes-card p-3 mb-4 rounded-3" style={{ background: '#fef3c7' }}>
                                    <h6 className="fw-semibold mb-2"><i className="ri-file-text-line me-1"></i>Motif</h6>
                                    <p className="mb-0">{selectedAvoir.notes}</p>
                                </div>
                            )}

                            <h6 className="fw-semibold mb-3">Lignes de l'avoir</h6>
                            {loadingLignes ? (
                                <div className="text-center py-4">
                                    <Spinner animation="border" variant="warning" size="sm" />
                                    <p className="mt-2 text-muted">Chargement des lignes...</p>
                                </div>
                            ) : (
                                <div className="table-responsive">
                                    <Table className="align-middle mb-0">
                                        <thead className="bg-light">
                                            <tr>
                                                <th className="ps-4">Référence</th>
                                                <th>Produit</th>
                                                <th className="text-center">Qté</th>
                                                <th className="text-end">Prix HT</th>
                                                <th className="text-end">Total HT</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {selectedAvoirLignes.length > 0 ? (
                                                selectedAvoirLignes.map((ligne, idx) => (
                                                    <tr key={idx}>
                                                        <td className="ps-4"><span className="reference-badge">{ligne.reference}</span></td>
                                                        <td className="fw-semibold">{ligne.designation}</td>
                                                        <td className="text-center">{ligne.quantite}</td>
                                                        <td className="text-end">{formatCurrency(ligne.prix_unitaire_ht)}</td>
                                                        <td className="text-end fw-bold text-primary">{formatCurrency(ligne.quantite * ligne.prix_unitaire_ht)}</td>
                                                    </tr>
                                                ))
                                            ) : (
                                                <tr>
                                                    <td colSpan="5" className="text-center py-4">
                                                        <p className="text-muted mb-0">Aucune ligne disponible</p>
                                                    </td>
                                                </tr>
                                            )}
                                        </tbody>
                                    </Table>
                                </div>
                            )}

                            <div className="summary-card p-3 mt-4 rounded-3">
                                <h6 className="fw-semibold mb-3 text-primary">Récapitulatif</h6>
                                <div className="d-flex justify-content-between mb-2">
                                    <span>Total HT</span>
                                    <span className="fw-bold">{formatCurrency(selectedAvoir.montant_ht)}</span>
                                </div>
                                <div className="d-flex justify-content-between mb-2">
                                    <span>TVA ({taxes.tva_active ? taxes.tva_taux : 0}%)</span>
                                    <span className="fw-bold text-warning">{formatCurrency(selectedAvoir.montant_tva)}</span>
                                </div>
                                <div className="d-flex justify-content-between mb-2">
                                    <span>CSS ({taxes.css_active ? taxes.css_taux : 0}%)</span>
                                    <span className="fw-bold text-info">{formatCurrency(selectedAvoir.montant_css)}</span>
                                </div>
                                <hr />
                                <div className="d-flex justify-content-between">
                                    <span className="h6 fw-bold">Total TTC</span>
                                    <span className="h6 fw-bold text-danger">-{formatCurrency(selectedAvoir.net_a_payer)}</span>
                                </div>
                            </div>
                        </>
                    )}
                </Modal.Body>
                <Modal.Footer className="border-0 pb-4 px-4">
                    <Button variant="light" onClick={() => setShowModal(false)} className="rounded-pill px-4">Fermer</Button>
                    <Button variant="warning" onClick={() => handlePrintAvoir(selectedAvoir)} className="rounded-pill px-4">
                        <i className="ri-printer-line me-2"></i> Imprimer
                    </Button>
                </Modal.Footer>
            </Modal>

            <style>{`
                .kpi-card {
                    background: white;
                    border-radius: 0px;
                    padding: 1rem;
                    display: flex;
                    align-items: center;
                    gap: 1rem;
                    box-shadow: 0 1px 3px rgba(0,0,0,0.05);
                    transition: all 0.2s ease;
                    height: 100%;
                    border-left: 2px solid;
                }
                .kpi-card:hover {
                    transform: translateY(-2px);
                    box-shadow: 0 8px 20px rgba(0,0,0,0.08);
                }
                .kpi-warning { border-left-color: #f59e0b; }
                .kpi-info { border-left-color: #0dcaf0; }
                .kpi-primary { border-left-color: #4361ee; }
                .kpi-success { border-left-color: #10b981; }
                
                .kpi-icon {
                    width: 48px;
                    height: 48px;
                    border-radius: 12px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                }
                .kpi-warning .kpi-icon { background: #fffbeb; color: #f59e0b; }
                .kpi-info .kpi-icon { background: #e2f3f5; color: #0dcaf0; }
                .kpi-primary .kpi-icon { background: #e8f0fe; color: #4361ee; }
                .kpi-success .kpi-icon { background: #ecfdf5; color: #10b981; }
                
                .kpi-content {
                    flex: 1;
                }
                .kpi-label {
                    font-size: 0.7rem;
                    text-transform: uppercase;
                    color: #6c757d;
                    font-weight: 600;
                    letter-spacing: 0.5px;
                }
                .kpi-value {
                    font-size: 1.1rem;
                    font-weight: 700;
                    margin: 0.25rem 0;
                    color: #1a3c5e;
                }
                .kpi-unit {
                    font-size: 0.7rem;
                    color: #6c757d;
                }
                
                .action-icon {
                    width: 32px;
                    height: 32px;
                    border-radius: 8px;
                    display: inline-flex;
                    align-items: center;
                    justify-content: center;
                    background: #f8f9fa;
                    border: 1px solid #e9ecef;
                    color: #6c757d;
                    transition: all 0.2s;
                    cursor: pointer;
                }
                .action-icon:hover {
                    background: #f59e0b;
                    border-color: #f59e0b;
                    color: white;
                }
                .action-icon-primary:hover {
                    background: #4361ee;
                    border-color: #4361ee;
                    color: white;
                }
                
                .table-link {
                    color: #f59e0b;
                    text-decoration: none;
                    font-weight: 600;
                    cursor: pointer;
                }
                .table-link:hover {
                    text-decoration: underline;
                }
                
                .reference-badge {
                    background: #f8f9fa;
                    padding: 0.2rem 0.6rem;
                    border-radius: 6px;
                    font-size: 0.7rem;
                    font-family: monospace;
                }
                
                .summary-card {
                    background: #f8f9fa;
                    border: 1px solid #e9ecef;
                    border-radius: 12px;
                }
                
                .info-card {
                    background: #f8f9fa;
                    border: 1px solid #e9ecef;
                    border-radius: 12px;
                }
                
                .notes-card {
                    border-left: 3px solid #f59e0b;
                }
                
                .rounded-3 {
                    border-radius: 12px !important;
                }
                .fs-10 {
                    font-size: 0.625rem;
                }
                .fs-14 {
                    font-size: 0.875rem;
                }
                .fs-24 {
                    font-size: 1.5rem;
                }
                .modern-modal .modal-content {
                    border: none;
                    border-radius: 24px;
                    overflow: hidden;
                }
            `}</style>
        </React.Fragment>
    );
}